import { DashboardChartBox } from '../dashboard-chart-box.tsx'
import { useGetDailyPnlQuery } from '../../../../../apis/statistics/statistics-api.api.ts'
import { EChartsOption } from 'echarts'
import ReactECharts from 'echarts-for-react'
import { TopLevelFormatterParams } from 'echarts/types/dist/shared'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectDashboardInput } from '../../../../../redux/slices/dashboard-input.slice.ts'
import dayjs from 'dayjs'
import { yAxisMaxMin } from './pnl-chart-utils.ts'
import echartsTheme from '../../../../../styles/echarts/echarts-theme.json'
import { selectThemeStyle } from '../../../../../features/setting/setting.slice.ts'

interface PnlChartProps {
  bookId: string
}

export const PnlChart: React.FC<PnlChartProps> = ({ bookId }) => {
  const dashboardInput = useAppSelector(selectDashboardInput)
  const themeStyle = useAppSelector(selectThemeStyle)

  const { data } = useGetDailyPnlQuery({
    bookId: bookId,
    startDate: dayjs(dashboardInput.dateRange[0]).format('YYYY-MM-DD'),
    endDate: dayjs(dashboardInput.dateRange[1]).format('YYYY-MM-DD'),
  })

  if (!data) return <></>

  // Ensure data has at least 20 entries
  const requiredLength = 20
  const missingLength = requiredLength - data.data.personalDailyPnls.length

  // Create a new array based on the original data
  const extendedData = [...data.data.personalDailyPnls]

  if (missingLength > 0) {
    const firstDate =
      data.data.personalDailyPnls.length > 0
        ? new Date(data.data.personalDailyPnls[0].date)
        : new Date()
    for (let i = 1; i <= missingLength; i++) {
      const newDate = new Date(firstDate)
      newDate.setDate(firstDate.getDate() - i)
      extendedData.unshift({
        date: newDate.toISOString().split('T')[0], // Format date as YYYY-MM-DD
        pnl: 0,
        returnOnBP: 0,
        returnOnGMV: 0,
        cumulativePnl: 0,
        drawdown: 0,
      })
    }
  }

  // Calculate mean and standard deviations
  const pnlValues = data.data.personalDailyPnls.map((d) => d.pnl)
  const meanPnl =
    pnlValues.reduce((sum, value) => sum + value, 0) / pnlValues.length
  const stdDevPnl = Math.sqrt(
    pnlValues.reduce((sum, value) => sum + Math.pow(value - meanPnl, 2), 0) /
      pnlValues.length
  )

  const plus1StdDev = (meanPnl + stdDevPnl).toFixed(2)
  const minus1StdDev = (meanPnl - stdDevPnl).toFixed(2)
  const plus2StdDev = (meanPnl + 2 * stdDevPnl).toFixed(2)
  const minus2StdDev = (meanPnl - 2 * stdDevPnl).toFixed(2)

  const labelCount = 30 // desired number of labels
  const interval = Math.max(1, Math.floor(extendedData.length / labelCount))

  const option: EChartsOption = {
    grid: {
      left: 0,
      right: 30,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: extendedData.map((d) => d.date),
      axisLabel: {
        color: themeStyle.sumText,
        rotate: 60,
        interval,
        formatter: (value: string, index: number) =>
          index % 2 === 0 ? value : '',
      },
    },
    yAxis: [
      {
        type: 'value',
        name: 'PnL ($)',
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        axisLabel: {
          color: themeStyle.sumText,
        },
        ...yAxisMaxMin,
      },
      {
        type: 'value',
        name: 'Return (%)',
        position: 'right',
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        axisLabel: {
          color: themeStyle.sumText,
        },
        ...yAxisMaxMin,
      },
    ],
    legend: {
      data: [
        'Return on BP',
        'Return on GMV',
        'PnL',
        '+1 Std Dev',
        '-1 Std Dev',
        '+2 Std Dev',
        '-2 Std Dev',
      ],
      selected: {
        PnL: true,
        'Return on BP': false,
        'Return on GMV': false,
        '+1 Std Dev': false,
        '-1 Std Dev': false,
        '+2 Std Dev': false,
        '-2 Std Dev': false,
      },
      top: 'bottom',
    },
    tooltip: {
      show: true,
      trigger: 'axis',
      extraCssText: 'text-align: left;',
      formatter: (
        params: TopLevelFormatterParams | TopLevelFormatterParams[]
      ) => {
        const paramArr = Array.isArray(params) ? params : [params]
        // axisValue is not typed on TopLevelFormatterParams, but is present at runtime for axis tooltips.
        // Use type assertion to access it safely.
        const axisValue =
          (paramArr[0] as { axisValue?: string; axisValueLabel?: string })
            .axisValueLabel ??
          (paramArr[0] as { axisValue?: string; axisValueLabel?: string })
            .axisValue ??
          (paramArr[0] as { value?: string | number }).value ??
          ''
        let tooltipText = `${axisValue}<br/>`
        paramArr.forEach((param) => {
          const value = (param as { value?: string | number }).value
          const marker = (param as { marker?: string }).marker || ''
          const seriesName = (param as { seriesName?: string }).seriesName || ''
          if (seriesName === 'PnL' || seriesName.includes('Std Dev')) {
            tooltipText += `${marker} ${seriesName}: $${Number(
              value
            ).toLocaleString(undefined, { maximumFractionDigits: 0 })}<br/>`
          } else if (
            seriesName === 'Return on BP' ||
            seriesName === 'Return on GMV'
          ) {
            tooltipText += `${marker} ${seriesName}: ${Number(
              value
            ).toLocaleString(undefined, { maximumFractionDigits: 2 })}%<br/>`
          } else {
            tooltipText += `${marker} ${seriesName}: ${Number(
              value
            ).toLocaleString(undefined, { maximumFractionDigits: 0 })}<br/>`
          }
        })
        return tooltipText
      },
    },
    series: [
      {
        type: 'line',
        name: 'Return on BP',
        symbol: 'circle', // show dot for every point
        yAxisIndex: 1,
        data: extendedData.map((d) => d.returnOnBP * 100),
      },
      {
        type: 'line',
        name: 'Return on GMV',
        symbol: 'circle', // show dot for every point
        yAxisIndex: 1,
        data: extendedData.map((d) => (d.returnOnGMV * 100).toFixed(2)),
      },
      {
        type: 'bar',
        name: 'PnL',
        data: extendedData.map((d) => d.pnl),
        itemStyle: {
          color: (params) => {
            const value = params.value
            if (typeof value === 'number') {
              return value >= 0 ? '#67b365' : '#f26466'
            }
            return ''
          },
        },
      },
      {
        type: 'line',
        name: '+1 Std Dev',
        symbol: 'circle', // show dot for every point
        data: Array(extendedData.length).fill(plus1StdDev),
        lineStyle: { type: 'dashed' },
      },
      {
        type: 'line',
        name: '-1 Std Dev',
        symbol: 'circle', // show dot for every point
        data: Array(extendedData.length).fill(minus1StdDev),
        lineStyle: { type: 'dashed' },
      },
      {
        type: 'line',
        name: '+2 Std Dev',
        symbol: 'circle', // show dot for every point
        data: Array(extendedData.length).fill(plus2StdDev),
        lineStyle: { type: 'dashed' },
      },
      {
        type: 'line',
        name: '-2 Std Dev',
        symbol: 'circle', // show dot for every point
        data: Array(extendedData.length).fill(minus2StdDev),
        lineStyle: { type: 'dashed' },
      },
    ],
  }

  return (
    <DashboardChartBox title={'PnL chart +/- 1SD, 2SD'}>
      <ReactECharts
        option={option}
        style={{ height: '400px' }}
        theme={echartsTheme.themeName}
      />
    </DashboardChartBox>
  )
}
